/**
 * Closed-loop scoring → targeted rewrite → reviewable diff → scored
 * variations for the Content Engine.
 *
 * Fixes the auditor findings that the score and the rewrite were two
 * disconnected features: the score said "hook is weak" but nothing turned
 * that into a rewrite, rewrites clobbered the draft with no old→new diff,
 * and "variations" were a single deterministic string. This module closes
 * the loop:
 *
 *   scoreBreakdown ─▶ weakestDimensions ─▶ buildRewriteConstraints
 *        ▲                                        │
 *        │                                        ▼
 *   applyRewrite ◀── reviewable line diff ◀── rewriteForFix (real worker
 *        (approve)      (never clobbers)      via generateAsset, sealed
 *                                             with provenance for lineage)
 *
 * LAYERING, not duplication: the five public dimensions are explanations
 * layered over the shipped heuristic scorer in `lib/crm/contentScore`
 * (hook → hookStrength, format → readability, authenticity → voiceMatch,
 * length → lengthFit) plus one spec-aware CTA-clarity heuristic. Same text
 * + spec + voice always earns the same breakdown — deterministic, no
 * network, no clock — so the meter is stable and tests stay green.
 *
 * The ONLY side-effectful exports are `rewriteForFix` and
 * `generateVariations`, whose single side effect is `generateAsset`'s call
 * through the REAL worker seam (`lib/agent/argoWorker`) when a worker is
 * injected; absent/failed workers degrade to the deterministic fallback
 * with honest `usedWorker: false` provenance. No customer data is ever
 * fabricated — every fact flows from the caller-supplied GenContext.
 */

import { scoreDraft, type Platform } from '../crm/contentScore';
import {
  generateAsset,
  FORMAT_SPECS,
  type ContentWorker,
  type FormatSpec,
  type GenContext,
  type VoiceProfile,
} from './generate';
import {
  buildProvenance,
  deepLinkFor,
  provenanceDigest,
  type ProposalProvenance,
} from '../agent/provenance';
import { fnvHash } from '../ledger/verifyChain';

// ---------------------------------------------------------------------------
// Dimensions
// ---------------------------------------------------------------------------

/** The five operator-facing axes of the closed scoring loop. */
export type Dimension =
  | 'readability'
  | 'hookStrength'
  | 'ctaClarity'
  | 'voiceMatch'
  | 'lengthFit';

/** Fixed render/report order so the meter never reshuffles. */
export const DIMENSIONS: readonly Dimension[] = [
  'readability',
  'hookStrength',
  'ctaClarity',
  'voiceMatch',
  'lengthFit',
];

/** Human labels for the meter. */
export const DIMENSION_LABELS: Record<Dimension, string> = {
  readability: 'Readability',
  hookStrength: 'Hook strength',
  ctaClarity: 'CTA clarity',
  voiceMatch: 'Voice match',
  lengthFit: 'Length fit',
};

/** One dimension's normalized score (0–100) with its plain-English WHY. */
export interface DimensionScore {
  score: number;
  why: string;
}

/** The full breakdown: 0–100 total + per-dimension score & why. */
export interface ScoreBreakdown {
  total: number;
  dims: Record<Dimension, DimensionScore>;
}

// ---------------------------------------------------------------------------
// scoreBreakdown — layered over lib/crm/contentScore
// ---------------------------------------------------------------------------

/** contentScore grades against a platform; map each format to its band. */
function platformFor(spec: FormatSpec): Platform {
  return spec.format === 'xThread' || spec.format === 'adCopy' || spec.format === 'landingHero'
    ? 'x'
    : 'linkedin';
}

const clamp100 = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/** CTA phrases + reply-invites the clarity heuristic recognizes. */
const CTA_LINE =
  /\b(sign up|buy|shop|order|register|subscribe|book|get started|try|join|download|reply|comment|share|dm|reach out|visit|learn more|check out|let'?s talk|grab|claim)\b|https?:\/\//i;

function nonEmptyLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

/**
 * Spec-aware CTA clarity: exactly ONE ask, sitting at the end of a draft
 * that delivered value first. contentScore's softSell covers placement but
 * not presence — a draft with NO ask scores full softSell marks — so this
 * dimension is the missing "is the reader told what to do?" check.
 */
function scoreCtaClarity(text: string, spec: FormatSpec): DimensionScore {
  const lines = nonEmptyLines(text);
  if (lines.length === 0) {
    return { score: 0, why: 'Empty draft — no call to action to judge.' };
  }
  if (!spec.ctaRequired) {
    return { score: 100, why: 'This format does not require a CTA.' };
  }
  const ctaIdxs = lines
    .map((l, i) => (CTA_LINE.test(l) || /\?\s*$/.test(l) ? i : -1))
    .filter((i) => i >= 0);
  if (ctaIdxs.length === 0) {
    return {
      score: 15,
      why: 'No clear call to action — the reader is never told what to do next.',
    };
  }
  if (lines.length <= 2) {
    return {
      score: 35,
      why: 'The ask IS the post — there is no value before the CTA.',
    };
  }
  if (ctaIdxs.length === 1 && ctaIdxs[0] === lines.length - 1) {
    return { score: 95, why: 'One clear CTA on the final line — value first, ask last.' };
  }
  if (ctaIdxs.length === 1) {
    return {
      score: 55,
      why: 'A single CTA exists but sits before the end — move the ask to the last line.',
    };
  }
  return {
    score: 45,
    why: `${ctaIdxs.length} competing asks — pick ONE CTA and cut the rest.`,
  };
}

/**
 * Grade a draft on the five loop dimensions, each 0–100 with a non-empty
 * WHY, layered over the shipped `scoreDraft` heuristics plus the voice
 * profile's own banned phrases. Deterministic and pure.
 */
export function scoreBreakdown(
  text: string,
  spec: FormatSpec,
  voice: VoiceProfile,
): ScoreBreakdown {
  const base = scoreDraft(text, { platform: platformFor(spec) });
  const dim = (key: string) => base.dimensions.find((d) => d.key === key);
  const pct = (key: string): number => {
    const d = dim(key);
    return d && d.max > 0 ? clamp100((d.score / d.max) * 100) : 0;
  };
  const note = (key: string, fallback: string): string => dim(key)?.note || fallback;

  // voiceMatch = authenticity + THIS voice profile's banned phrases.
  const lower = text.toLowerCase();
  const bannedHits = voice.bannedPhrases.filter((p) => lower.includes(p.toLowerCase()));
  let voiceScore = pct('authenticity');
  let voiceWhy = note('authenticity', 'No voice yet.');
  if (bannedHits.length > 0) {
    voiceScore = clamp100(voiceScore - bannedHits.length * 15);
    voiceWhy = `Uses banned "${voice.name}" phrases: ${bannedHits.slice(0, 3).join(', ')}. ${voiceWhy}`;
  }

  // lengthFit = platform band + this format's hard character limit.
  let lengthScore = pct('length');
  let lengthWhy = note('length', 'Empty draft.');
  if (spec.charLimit !== undefined && spec.format !== 'xThread' && text.length > spec.charLimit) {
    lengthScore = Math.min(lengthScore, 20);
    lengthWhy = `${text.length} chars blows the ${spec.charLimit}-char ${spec.format} limit. ${lengthWhy}`;
  }

  const cta = scoreCtaClarity(text, spec);

  const dims: Record<Dimension, DimensionScore> = {
    readability: { score: pct('format'), why: note('format', 'No text to format.') },
    hookStrength: { score: pct('hook'), why: note('hook', 'No first line to hook with.') },
    ctaClarity: cta,
    voiceMatch: { score: voiceScore, why: voiceWhy },
    lengthFit: { score: lengthScore, why: lengthWhy },
  };

  const total = clamp100(
    DIMENSIONS.reduce((sum, d) => sum + dims[d].score, 0) / DIMENSIONS.length,
  );
  return { total, dims };
}

// ---------------------------------------------------------------------------
// weakestDimensions + buildRewriteConstraints — the score→rewrite bridge
// ---------------------------------------------------------------------------

/**
 * Dimensions scoring below `threshold` (default 70), worst-first, so the
 * UI's "Fix this" buttons and the rewrite constraints target the biggest
 * problem first. Ties keep the fixed DIMENSIONS order (stable sort).
 */
export function weakestDimensions(breakdown: ScoreBreakdown, threshold = 70): Dimension[] {
  return DIMENSIONS.filter((d) => breakdown.dims[d].score < threshold).sort(
    (a, b) => breakdown.dims[a].score - breakdown.dims[b].score,
  );
}

/** Per-dimension worker directives — the "fix this" vocabulary. */
const CONSTRAINT_BY_DIM: Record<Dimension, string> = {
  readability:
    'Improve readability: break dense paragraphs into one idea per line with whitespace between thoughts',
  hookStrength:
    'Strengthen the opening hook to a stat-led or scene-led first line — cut every warm-up opener',
  ctaClarity:
    'End with exactly ONE clear CTA on the final line telling the reader what to do next',
  voiceMatch:
    'Match the brand voice: first person, plain spoken language, zero corporate-polish or banned phrases',
  lengthFit:
    "Fit the format's target length — trim or expand to the platform sweet spot without losing the substance",
};

/**
 * Turn failing dimensions into concrete worker constraints, each carrying
 * the breakdown's WHY so the rewrite targets the actual diagnosis, not a
 * generic tip. Pure and deterministic.
 */
export function buildRewriteConstraints(
  dims: Dimension[],
  breakdown: ScoreBreakdown,
): string[] {
  return dims.map((d) => {
    const why = breakdown.dims[d]?.why?.trim();
    return why ? `${CONSTRAINT_BY_DIM[d]} (diagnosis: ${why})` : CONSTRAINT_BY_DIM[d];
  });
}

// ---------------------------------------------------------------------------
// Reviewable diff — the anti-clobber mechanic
// ---------------------------------------------------------------------------

/** One line of the reviewable rewrite diff. */
export interface DiffLine {
  kind: 'keep' | 'del' | 'add';
  line: string;
}

/**
 * The old→new story for one rewrite — same trust mechanic as
 * `agent/actionDiff` / `crm/inlineEditDiff`: the operator sees exactly
 * what changes BEFORE anything is applied. Carries both full texts so
 * apply/undo are trivial and lossless.
 */
export interface DiffResult {
  original: string;
  proposed: string;
  lines: DiffLine[];
  /** Render-ready one-liner, e.g. "3 lines changed · 412 → 388 chars". */
  summary: string;
}

/**
 * Deterministic line diff: shared prefix/suffix lines are `keep`, the
 * middle is `del` (original) then `add` (proposed). Simple by design —
 * it is a review surface, not a patch format.
 */
export function buildRewriteDiff(original: string, proposed: string): DiffResult {
  const a = original.split('\n');
  const b = proposed.split('\n');
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const lines: DiffLine[] = [
    ...a.slice(0, start).map((line): DiffLine => ({ kind: 'keep', line })),
    ...a.slice(start, endA).map((line): DiffLine => ({ kind: 'del', line })),
    ...b.slice(start, endB).map((line): DiffLine => ({ kind: 'add', line })),
    ...a.slice(endA).map((line): DiffLine => ({ kind: 'keep', line })),
  ];
  const changed = Math.max(endA - start, endB - start);
  return {
    original,
    proposed,
    lines,
    summary:
      changed === 0
        ? 'No changes'
        : `${changed} line${changed === 1 ? '' : 's'} changed · ${original.length} → ${proposed.length} chars`,
  };
}

/**
 * The ACCEPT path: apply a reviewed diff to the original text. Pure —
 * never mutates, same inputs same output. Throws if the diff was built
 * against different text (a stale approve must fail loudly, never
 * silently clobber the operator's newer draft).
 */
export function applyRewrite(original: string, diff: DiffResult): string {
  if (diff.original !== original) {
    throw new Error('Rewrite diff does not apply: the draft changed since it was proposed.');
  }
  return diff.proposed;
}

// ---------------------------------------------------------------------------
// rewriteForFix — score → worker → diff → seal
// ---------------------------------------------------------------------------

/** Provenance seal on a rewrite/variation, ready for the Decision Ledger. */
export interface RewriteSeal {
  provenance: ProposalProvenance;
  digest: string;
  /** True only when the REAL worker wrote the proposed text. */
  usedWorker: boolean;
  model: string;
  promptHash: string;
}

export interface RewriteResult {
  proposed: string;
  diff: DiffResult;
  sealed: RewriteSeal;
}

/**
 * Rewrite a draft to fix specific failing dimensions. Routes through the
 * SAME `generateAsset` path as first-draft generation (real worker when
 * injected + configured; deterministic fallback otherwise), returns a
 * reviewable diff — NEVER a silent overwrite — and a provenance seal
 * whose confidence basis states the before→after heuristic scores.
 */
export async function rewriteForFix(
  text: string,
  spec: FormatSpec,
  ctx: GenContext,
  dims: Dimension[],
  deps: { worker?: ContentWorker } = {},
): Promise<RewriteResult> {
  const before = scoreBreakdown(text, spec, ctx.voice);
  const constraints = buildRewriteConstraints(dims, before);
  const brief = [
    `Rewrite the following ${spec.format} draft. Apply every constraint:`,
    ...constraints.map((c) => `- ${c}`),
    '',
    'Keep every fact and everything that already works. Original draft:',
    '"""',
    text,
    '"""',
  ].join('\n');

  const asset = await generateAsset(brief, spec.format, ctx, deps);
  // Anti-clobber guard: an empty proposal can never blank the draft.
  const proposed = asset.text.trim().length > 0 ? asset.text : text;
  const after = scoreBreakdown(proposed, spec, ctx.voice);
  const diff = buildRewriteDiff(text, proposed);

  const id = `rewrite-${asset.provenance.promptHash}`;
  const provenance = buildProvenance({
    evidence: [
      {
        kind: 'deliverable',
        id,
        label: `Targeted rewrite: ${dims.map((d) => DIMENSION_LABELS[d]).join(', ')}`,
        deepLink: deepLinkFor('deliverable', id),
      },
    ],
    confidence: {
      score: after.total / 100,
      basis: `Heuristic score ${before.total} → ${after.total}/100 after targeting ${dims.join(', ')} via ${asset.provenance.model}.`,
    },
  });

  return {
    proposed,
    diff,
    sealed: {
      provenance,
      digest: provenanceDigest(provenance),
      usedWorker: asset.provenance.usedWorker,
      model: asset.provenance.model,
      promptHash: asset.provenance.promptHash,
    },
  };
}

// ---------------------------------------------------------------------------
// Variations — real scored, lineage-linked siblings
// ---------------------------------------------------------------------------

/** The distinct editorial angles, cycled (suffixed) past four. */
const ANGLES: ReadonlyArray<{ angle: string; directive: string }> = [
  {
    angle: 'shorter',
    directive: 'Make it noticeably shorter — cut every line that does not earn its place.',
  },
  {
    angle: 'punchier',
    directive: 'Make it punchier — shorter sentences, harder verbs, one quotable line.',
  },
  {
    angle: 'formal',
    directive: 'Make it more formal and measured — executive register, no slang.',
  },
  {
    angle: 'different-hook',
    directive: 'Open with a COMPLETELY different hook — a stat, scene, or contrarian claim.',
  },
];

/** One scored, lineage-linked sibling of a brief. */
export interface Variation {
  text: string;
  /** Full five-dimension breakdown — every sibling arrives pre-scored. */
  score: ScoreBreakdown;
  /** All siblings of one run share this id — the A/B family key. */
  parentId: string;
  angle: string;
  lineage: {
    parentId: string;
    angle: string;
    promptHash: string;
    digest: string;
    usedWorker: boolean;
    model: string;
  };
}

/** Stable family id for one brief × format run. */
export function variationParentId(brief: string, format: string): string {
  return `content-${fnvHash('', `${format}\u0000${brief}`)}`;
}

/**
 * Generate `n` DISTINCT variations of one brief — each at a different
 * editorial angle, each scored with the full breakdown, each
 * lineage-linked to the shared parentId with its own provenance digest.
 * Real worker when injected; deterministic fallback otherwise. Angles
 * cycle with a numeric suffix past four so n=6 still yields six distinct
 * angle tags.
 */
export async function generateVariations(
  brief: string,
  format: string,
  ctx: GenContext,
  n: number,
  deps: { worker?: ContentWorker } = {},
): Promise<Variation[]> {
  const spec = FORMAT_SPECS[format];
  if (!spec) throw new Error(`Unknown content format: "${format}"`);
  const parentId = variationParentId(brief, format);

  const out: Variation[] = [];
  for (let i = 0; i < Math.max(0, n); i++) {
    const base = ANGLES[i % ANGLES.length];
    const round = Math.floor(i / ANGLES.length);
    const angle = round === 0 ? base.angle : `${base.angle}-${round + 1}`;
    const asset = await generateAsset(
      `${brief}\n\nVariation angle (${angle}): ${base.directive}`,
      format,
      ctx,
      deps,
    );
    const score = scoreBreakdown(asset.text, spec, ctx.voice);
    const provenance = buildProvenance({
      evidence: [
        {
          kind: 'deliverable',
          id: `${parentId}:${angle}`,
          label: `Variation ${angle} of ${format}`,
          deepLink: deepLinkFor('deliverable', `${parentId}:${angle}`),
        },
      ],
      confidence: {
        score: score.total / 100,
        basis: `Heuristic score ${score.total}/100 at angle "${angle}" via ${asset.provenance.model}.`,
      },
    });
    out.push({
      text: asset.text,
      score,
      parentId,
      angle,
      lineage: {
        parentId,
        angle,
        promptHash: asset.provenance.promptHash,
        digest: provenanceDigest(provenance),
        usedWorker: asset.provenance.usedWorker,
        model: asset.provenance.model,
      },
    });
  }
  return out;
}

/**
 * Pick the highest-total variation ("pick best"). Ties go to the earlier
 * sibling — stable and deterministic. Returns null for an empty list so
 * the UI renders an honest empty state instead of a fabricated winner.
 */
export function bestVariation(variations: Variation[]): Variation | null {
  let best: Variation | null = null;
  for (const v of variations) {
    if (best === null || v.score.total > best.score.total) best = v;
  }
  return best;
}
